// run-command-prompt-red-tests-section.ts
//
// The RED tests an implement/fix worker must turn green. A testcase ticket
// leaves failing tests on the mission branch; the pack's `fullstack-developer`
// agent reads them from a `RED_TESTS:` line and treats them as the spec (never
// weakened, skipped or rewritten to pass). Before this section the worker only
// saw its own ticket, so the agent fell back to `RED_TESTS: none`
// (team-ai-pack `workflows/autopilot/command-adapters.md`).
//
// Which testcases: the same pairing the scheduler's RED window uses — testcase
// X belongs to this ticket when the ticket is in `green(X)` (the nearest
// implement/fix forward from X), and X is `done` (its RED evidence was
// verified). Paths are X's `allowed_paths`: the testcase adapter may write only
// there, so the RED tests live under them. No schema change.
//
// Pure: the caller (supervisor-loop.ts) passes the projection it already holds.
import { buildRedWindowGraph, GREENING_ADAPTER_IDS } from "./scheduler-red-window-rule.js";
import type { Ticket } from "./schemas/ticket-schema.js";

export const RED_TESTS_SECTION_TITLE = "## Test RED cần làm xanh (RED_TESTS)";

/** Done testcases whose RED window this ticket closes, sorted by id. */
export function redTestcasesFor(
  ticket: Ticket,
  tickets: readonly Ticket[],
  ticketStatus: Readonly<Record<string, string>>,
): Ticket[] {
  if (!GREENING_ADAPTER_IDS.includes(ticket.command_adapter)) return [];
  const graph = buildRedWindowGraph(tickets, ticketStatus);
  return tickets
    .filter((t) => graph.isTestcase(t) && graph.status(t.id) === "done")
    .filter((t) => graph.green(t.id).includes(ticket.id))
    .sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * `""` for any adapter that turns nothing green (prompt byte-identical to
 * before). For implement/fix (leading `\n`, no trailing one, like the other
 * appended sections): the title, then `- RED_TESTS: <paths>` (or
 * `none` when no done testcase feeds this ticket), then one rule line, then a
 * `Dùng chung với:` line when other tickets green the same testcase.
 */
export function renderRedTestsSection(
  ticket: Ticket,
  tickets: readonly Ticket[],
  ticketStatus: Readonly<Record<string, string>>,
): string {
  if (!GREENING_ADAPTER_IDS.includes(ticket.command_adapter)) return "";
  const graph = buildRedWindowGraph(tickets, ticketStatus);
  const sources = redTestcasesFor(ticket, tickets, ticketStatus);
  const paths = [...new Set(sources.flatMap((t) => t.allowed_paths))];
  const lines = ["", RED_TESTS_SECTION_TITLE];
  if (paths.length === 0) {
    lines.push(
      "- RED_TESTS: none (không có ticket testcase đã xong nào đặt test đỏ cho ticket này)",
    );
    return lines.join("\n");
  }
  lines.push(`- RED_TESTS: ${paths.join(", ")}`);
  lines.push(
    `- Nguồn: testcase ${sources.map((t) => t.id).join(", ")} (done, RED đã verify). Test mà testcase để lại dưới các đường dẫn trên là spec: không sửa, skip hay làm yếu để qua. Chạy chúng trước khi sửa và sau khi sửa (phải xanh), dán cả hai output vào evidence.`,
  );
  // One testcase can be greened by several tickets: the earlier ones may already
  // have turned part of these tests green, so "red before" is not guaranteed.
  const sharedWith = [
    ...new Set(sources.flatMap((t) => graph.green(t.id)).filter((id) => id !== ticket.id)),
  ].sort();
  if (sharedWith.length > 0) {
    lines.push(
      `- Dùng chung với: ${sharedWith.join(", ")} — phần test của ticket đó có thể đã xanh; chỉ phần của ticket này phải đỏ trước khi sửa.`,
    );
  }
  return lines.join("\n");
}
